import React from 'react';
import PropTypes from 'prop-types';
import { Quote, Vehicle } from '@sm360/widget-commons-vehicle';
import { LocaleService, WindowProxy, ContentZonesService } from '@sm360/widget-commons-core-services';
import { EVENTS as EVENTS_COMMONS_GENERAL, ADOBE_TAGS, AdobeService } from '@sm360/widget-commons-general';
import GarageCashPurchase from 'commons-garage/components/GarageCashPurchase/GarageCashPurchase.jsx';
import GarageFinance from 'commons-garage/components/GarageFinance/GarageFinance.jsx';
import 'commons-garage/components/GarageVehicle/VehiclePayments/VehiclePayments.scss';

const VehiclePayments = ({
    quote,
    vehicle,
    isOfficialAmountExpired,
    hasAdobeTagging,
    displayCreditRate,
    displayTradeInCreditRate,
}) => {
    const isCarAvailable = vehicle.isAvailable();
    const isQuoteInPendingStatus = quote.isQuoteInPending();
    const isQuoteInApprovalStatus = quote.isQuoteInApproval();
    const isCashPurchase = quote.cashPurchase !== null;
    const checkoutRedirectionUrl = quote.getCheckoutUrl(quote?.orgUnitId);

    const isQuoteWithDeposit = !isQuoteInPendingStatus && isCarAvailable && !isQuoteInApprovalStatus;
    const isQuoteWithoutDeposit = isQuoteInPendingStatus && !isQuoteInApprovalStatus;
    const isQuoteExpired = quote.isExpired;

    let conditionType;

    if (vehicle?.getIsVehicleNew()) {
        if (vehicle?.getVehicleId()) {
            conditionType = 'new';
        } else {
            conditionType = 'catalog';
        }
    } else {
        conditionType = 'used';
    }

    const disableCheckoutCZ = ContentZonesService.getContentZone(
        'widget.transaction',
        `widget.transaction.enable.checkout.for.${conditionType}`
    );

    const disableCheckoutCZValue = disableCheckoutCZ && disableCheckoutCZ.getValue() === 'false';

    const renderedContent = [];
    const renderedCta = [];
    let ctaButton = null;

    const summaryButton = (
        <a href={checkoutRedirectionUrl} className="vehicle-summary sr-button-1 is-small">
            {LocaleService.t('GARAGE.VEHICLE_STATUS.SUMMARY')}
        </a>
    );
    // Switch to tradeIn bar if trade vehicle official value is expired
    const switchToTradeInBar = () => {
        const $window = WindowProxy.get();
        if ($window) {
            $window.document.dispatchEvent(new Event(EVENTS_COMMONS_GENERAL.SWITCH_TO_TRADEIN_BAR));
        }
    };

    const handleClick = () => {
        if (hasAdobeTagging) {
            AdobeService.eventTrigger(ADOBE_TAGS.GARAGE_CHECKOUT_CTA(quote?.vehicle), 'global-onclick');
        }
    };

    if (isQuoteWithDeposit) {
        ctaButton = (
            <a href={checkoutRedirectionUrl} className="sr-button-1 is-small" onClick={handleClick}>
                {LocaleService.t('GARAGE.VEHICLE_STATUS.SELECT')}
            </a>
        );

        if (isCashPurchase) {
            renderedContent.push(<GarageCashPurchase quote={quote} />);
            renderedCta.push(ctaButton);
        } else {
            renderedContent.push(
                <GarageFinance
                    quote={quote}
                    displayCreditRate={displayCreditRate}
                    displayTradeInCreditRate={displayTradeInCreditRate}
                />
            );
            renderedCta.push(ctaButton);
        }
    } else if (isQuoteWithoutDeposit) {
        ctaButton = (
            <a href={checkoutRedirectionUrl} className="sr-button-1 is-small is-right">
                {quote.status === Quote.StatusesV25.SOLD || quote.status === Quote.StatusesV25.DELIVERED
                    ? LocaleService.t('GARAGE.VEHICLE_STATUS.SUMMARY')
                    : LocaleService.t('GARAGE.VEHICLE_STATUS.FINALIZE')}
            </a>
        );
        // We need to push an empty span, since the layout is flexed with justify-content: space-between.
        renderedContent.push(<span />);
        renderedCta.push(ctaButton);
    } else if (isQuoteExpired || !isCarAvailable) {
        if (isQuoteInApprovalStatus) {
            renderedCta.push(summaryButton);
        } else {
            renderedContent.push(<span className="is-price">{LocaleService.t('GARAGE.UNAVAILABLE')}</span>);
        }
    } else if (isQuoteInApprovalStatus) {
        renderedCta.push(summaryButton);
    }

    const renderedElements = (
        <>
            <div className="garage-vehicle-payments-info">
                <div className="garage-vehicle-payments">{renderedContent}</div>
                {!disableCheckoutCZValue && (
                    <div className={`garage-vehicle-payments-button ${isOfficialAmountExpired && 'disabled'}`}>
                        {renderedCta}
                    </div>
                )}
            </div>
            {/* Disable the checkout button and display the official value expired message with link to open TradeIn bar */}
            {isOfficialAmountExpired && (
                <div className="official-value-expired">
                    <a
                        className="is-small"
                        onClick={switchToTradeInBar}
                        dangerouslySetInnerHTML={{
                            __html: LocaleService.t('SUMMARY.OFFICIAL_VALUE_EXPIRED_LABEL'),
                        }}
                    />
                </div>
            )}
        </>
    );

    return renderedElements;
};

VehiclePayments.propTypes = {
    quote: PropTypes.instanceOf(Quote.QuoteModelV25).isRequired,
    vehicle: PropTypes.instanceOf(Vehicle.VehicleModelV25).isRequired,
    isOfficialAmountExpired: PropTypes.bool.isRequired,
    hasAdobeTagging: PropTypes.bool,
    displayCreditRate: PropTypes.bool,
    displayTradeInCreditRate: PropTypes.bool,
};

VehiclePayments.defaultProps = {
    hasAdobeTagging: false,
    displayCreditRate: false,
    displayTradeInCreditRate: false,
};

VehiclePayments.displayName = 'VehiclePayments';

export default VehiclePayments;
